import { useState, useCallback } from 'react';
import { useChatbotSearch } from './useChatbotSearch';

export const useChatbot = ({ onResponse }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // LLM çağrısı artık SUNUCUDA (public/chatbot.php) — API anahtarı client bundle'a sızmaz.
  // Bu hook CMS'ten bağlam toplar, sistemi ve LLM'i chatbot.php yönetir.

  // Chatbot'a özel arama hook'u
  const chatbotSearch = useChatbotSearch();

  const buildContextFromSearchResults = useCallback((results) => {
    console.log('Building context from search results:', results);

    // Bağlantılar MUTLAK URL olsun: LLM göreli "/haberler/x"i bazen "http://haberler/x"e
    // bozuyordu; tam URL'yi aynen kopyalıyor. Köşeli parantezli tarih de kullanılmıyor
    // (markdown link parantezleriyle karışmasın).
    const SITE = 'https://serdivan.bel.tr';

    let context = "Serdivan Belediyesi'ne ait güncel bilgiler:\n\n";
    let hasData = false;
    
    // Haberler - API'den gelen yapıyı doğru oku (en yeni önce; sonuçlar zaten yayin_tarihi:desc)
    if (results.news && Array.isArray(results.news) && results.news.length > 0) {
      context += "GÜNCEL HABERLER (en yeniden eskiye):\n";
      results.news.slice(0, 8).forEach(news => {
        if (news && news.baslik) {
          const tarih = news.yayin_tarihi
            ? new Date(news.yayin_tarihi).toLocaleDateString('tr-TR')
            : (news.publishedAt ? new Date(news.publishedAt).toLocaleDateString('tr-TR') : '');
          const aciklama = news.aciklama || 'Açıklama mevcut değil';
          const link = news.slug ? ` Bağlantı: ${SITE}/haberler/${news.slug}` : '';
          context += `- ${news.baslik} (${tarih})${link} : ${aciklama.substring(0, 300)}...\n`;
          hasData = true;
        }
      });
      context += "\n";
    }
    
    // Duyurular - API'den gelen yapıyı doğru oku
    if (results.announcements && Array.isArray(results.announcements) && results.announcements.length > 0) {
      context += "GÜNCEL DUYURULAR:\n";
      results.announcements.slice(0, 8).forEach(ann => {
        if (ann && ann.baslik) {
          const tarih = ann.baslangic_tarihi ? new Date(ann.baslangic_tarihi).toLocaleDateString('tr-TR') : '';
          const icerik = ann.icerik || 'İçerik mevcut değil';
          context += `- ${ann.baslik} (${tarih}) Bağlantı: ${SITE}/duyurular : ${icerik.substring(0, 300)}...\n`;
          hasData = true;
        }
      });
      context += "\n";
    }
    
    // Etkinlikler - YALNIZCA bugün ve sonrası (yaklaşan), tarihe göre ARTAN sıralı.
    // Geçmiş etkinlikler "yaklaşan" diye sunulmasın; tarih filtresi burada (deterministik) yapılır.
    if (results.events && Array.isArray(results.events) && results.events.length > 0) {
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      const tomorrow = new Date(startOfToday);
      tomorrow.setDate(startOfToday.getDate() + 1);

      const upcoming = results.events
        .filter(e => e && e.name && e.create && new Date(e.create) >= startOfToday)
        .sort((a, b) => new Date(a.create) - new Date(b.create));

      if (upcoming.length > 0) {
        context += "YAKLAŞAN ETKİNLİKLER (bugün ve sonrası, tarihe göre sıralı):\n";
        upcoming.slice(0, 8).forEach(event => {
          const eventName = event.name;
          const eventType = event.events || 'Etkinlik';
          const eventLocation = event.location?.name || 'Konum belirtilmemiş';

          const eventDate = new Date(event.create);
          const dateStr = eventDate.toLocaleDateString('tr-TR');
          const dayName = eventDate.toLocaleDateString('tr-TR', { weekday: 'long' });
          let eventDateInfo;
          if (eventDate.toDateString() === startOfToday.toDateString()) eventDateInfo = `BUGÜN (${dateStr} - ${dayName})`;
          else if (eventDate.toDateString() === tomorrow.toDateString()) eventDateInfo = `YARIN (${dateStr} - ${dayName})`;
          else eventDateInfo = `${dateStr} (${dayName})`;

          const eventPrice = event.price === 0 ? 'Ücretsiz' : (event.price ? `${event.price} TL` : '');
          const eventQuota = event.quota ? `Kota: ${event.quota}` : '';
          const eventTicket = event.ticket || '';
          const link = event.slug ? ` Bağlantı: ${SITE}/etkinlik/${event.slug}` : '';

          let eventInfo = `${eventType}`;
          if (eventLocation && eventLocation !== 'Konum belirtilmemiş') eventInfo += ` - ${eventLocation}`;
          if (eventDateInfo) eventInfo += ` - ${eventDateInfo}`;
          if (eventPrice) eventInfo += ` - ${eventPrice}`;
          if (eventTicket) eventInfo += ` - ${eventTicket}`;
          if (eventQuota) eventInfo += ` - ${eventQuota}`;

          context += `- ${eventName}${link}: ${eventInfo}\n`;
        });
        context += "\n";
        hasData = true;
      } else {
        context += `YAKLAŞAN ETKİNLİKLER: Şu anda planlanmış yaklaşan bir etkinlik bulunmuyor. Tüm etkinlikler için etkinlik takvimi Bağlantı: ${SITE}/etkinlik-takvimi\n\n`;
        hasData = true;
      }
    }
    
    // Hizmetler - API'den gelen yapıyı doğru oku ve daha detaylı bilgi ver
    if (results.services && Array.isArray(results.services) && results.services.length > 0) {
      context += "BELEDİYE HİZMETLERİ:\n";
      results.services.slice(0, 20).forEach(service => { // Limit 20'ye çıkarıldı
        if (service && service.adi) {
          const aciklama = service.description || 'Hizmet bilgisi mevcut';
          const serviceType = service.serviceType || 'Genel Hizmet';
          const isOnline = service.isOnline ? ' (Online Hizmet)' : '';
          // Dış online hizmet linki varsa onu, yoksa hizmet detay sayfasını (mutlak) bağla.
          let hizmetLink = '';
          if (service.url) hizmetLink = /^https?:\/\//i.test(service.url) ? service.url : `${SITE}/${service.url.replace(/^\//, '')}`;
          else if (service.slug) hizmetLink = `${SITE}/hizmetler/${service.slug}`;
          const linkStr = hizmetLink ? ` Bağlantı: ${hizmetLink}` : '';

          // Açıklama çok uzunsa kısalt
          const shortDescription = aciklama.length > 150 ?
            aciklama.substring(0, 150) + '...' : aciklama;

          context += `- ${service.adi} (${serviceType})${isOnline}${linkStr} : ${shortDescription}\n`;
          hasData = true;
        }
      });
      context += "\n";
    }
    
    if (!hasData) {
      context += "Arama yapıldı ancak bu konuda güncel bilgi bulunamadı. Lütfen farklı anahtar kelimelerle tekrar deneyin.\n";
      console.log('No data found in search results');
    } else {
      console.log('Context built successfully with data');
    }
    
    return context;
  }, []);

  const sendMessage = useCallback(async (userMessage) => {
    setLoading(true);
    setError(null);

    try {
      console.log('Chatbot - Starting search for:', userMessage);
      
      // Türkçe sorgu analizi - hizmet sorguları da dahil edildi
      const queryAnalysis = {
        isEventQuery: /etkinlik|etkinlikler|event|events|program|aktivite|göster|sinema|film|animasyon|festival|organizasyon/i.test(userMessage),
        isTimeQuery: /bugün|yarın|hafta|gelecek|bu|gelecek hafta|bu hafta|haftasonu|week|today|tomorrow/i.test(userMessage),
        isGeneralQuery: /hangi|neler|ne var|mevcut|güncel|listele|var mı|ne yapılıyor/i.test(userMessage),
        isCinemaQuery: /sinema|film|animasyon|gösterim/i.test(userMessage),
        isTurkishEventQuery: /etkinlik|etkinlikler/i.test(userMessage), // Özel Türkçe etkinlik kontrolü
        isTodayTomorrowQuery: /bugün|yarın|today|tomorrow/i.test(userMessage), // Bugün/yarın kontrolü
        isServiceQuery: /hizmet|hizmetler|nasıl|başvuru|işlem|ruhsat|imar|vergi|ödeme|online|belge|izin|evlilik|nikah|serbim|meclis|müdürlük|belediye|başkan/i.test(userMessage), // Hizmet sorguları genişletildi
        // "Duyurular hakkında bilgi alabilir miyim" gibi sorular hiçbir tetikleyiciye
        // uymayınca dar arama 0 sonuç veriyordu -> haber/duyuru da geniş arama tetiklesin.
        isContentQuery: /duyuru|haber|karar|gündem|gundem/i.test(userMessage)
      };
      
      console.log('Query analysis:', queryAnalysis);

      // Önce arama sonuçlarını temizle
      chatbotSearch.clear();
      
      let searchResults = null;
      
      // Eğer etkinlik/tarih/hizmet sorusu ise veya Türkçe etkinlik sorusu ise, direkt olarak geniş arama yap
      if (queryAnalysis.isEventQuery || queryAnalysis.isTimeQuery || queryAnalysis.isGeneralQuery ||
          queryAnalysis.isTurkishEventQuery || queryAnalysis.isTodayTomorrowQuery || queryAnalysis.isServiceQuery ||
          queryAnalysis.isContentQuery) {
        console.log('Detected Turkish event/time/service query, doing broad search...');
        
        // Türkçe etkinlik sorularında veya bugün/yarın sorularında özel arama
        if (queryAnalysis.isTurkishEventQuery || queryAnalysis.isTodayTomorrowQuery) {
          console.log('Turkish event or today/tomorrow query detected, prioritizing Turkish terms...');
          searchResults = await chatbotSearch.searchWithTurkishPriority(userMessage);
        } else {
          // Önce eş anlamlı kelimelerle ara
          searchResults = await chatbotSearch.searchWithSynonyms(userMessage);
        }
        
        // Sonuçları kontrol et
        if (searchResults) {
          let totalResults = (searchResults.news?.length || 0) + 
                            (searchResults.announcements?.length || 0) + 
                            (searchResults.events?.length || 0) + 
                            (searchResults.services?.length || 0);
          
          // Etkinlik verisi yoksa, tüm veriyi getir
          if (totalResults === 0 || searchResults.events?.length === 0) {
            console.log('No event results, getting all data...');
            searchResults = await chatbotSearch.searchAll('');
          }
        }
      } else {
        // Normal arama süreci
        searchResults = await chatbotSearch.searchAll(userMessage);
        
        if (searchResults) {
          let totalResults = (searchResults.news?.length || 0) + 
                            (searchResults.announcements?.length || 0) + 
                            (searchResults.events?.length || 0) + 
                            (searchResults.services?.length || 0);
          
          if (totalResults === 0) {
            console.log('No results found, trying synonyms...');
            searchResults = await chatbotSearch.searchWithSynonyms(userMessage);
          }
        }
      }

      // Final results'ı searchResults'tan al veya fallback olarak chatbotSearch.results kullan
      const finalResults = searchResults || chatbotSearch.results;
      console.log('Final search results for AI:', finalResults);
      
      const context = buildContextFromSearchResults(finalResults);
      
      // LLM çağrısı SUNUCUDA (anahtar gizli): CMS bağlamını /chatbot.php'ye gönder.
      // Sistem promptu + Gemini/Groq + cache orada yönetilir.
      const resp = await fetch('/chatbot.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMessage, context }),
      });
      if (!resp.ok) throw new Error('chatbot.php HTTP ' + resp.status);
      const data = await resp.json();
      const text = (data && data.reply) ? data.reply : 'Üzgünüm, şu anda cevap veremiyorum. Lütfen tekrar deneyin.';
      onResponse(text);
    } catch (err) {
      console.error('Chatbot error:', err);
      setError(err.message);
      onResponse('Üzgünüm, bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  }, [chatbotSearch, onResponse, buildContextFromSearchResults]);

  return {
    sendMessage,
    loading,
    error
  };
};